import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Fetchexecutequery,
  Fetchupdatequery,
} from "../../../services/process/BRS1/brsService7";
import { headers, baseUrl } from "../../../services/common/credentials";
import { CreateData } from "../../../services/process/process_data/process_data";
import {
  Globe, Users, UserPlus, GraduationCap, Image, Check, Save, Upload,
  Link as LinkIcon, Plus, Minus, AlertCircle, CheckCircle, ChevronDown, ChevronUp,
  FileText, Eye, Building, BookOpen,
} from "lucide-react";

// ── Helpers ────────────────────────────────────────────────────────────────────
const getUserCredentials = () =>
  JSON.parse(sessionStorage.getItem("userCredentials") || "{}");

const getFormattedDate = () =>
  new Date().toISOString().slice(0, 19).replace("T", " ");

const FONT = "'Inter', 'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, sans-serif";

// ── Design tokens ──────────────────────────────────────────────────────────────
const T = {
  navy:        "#0a2352",
  navyDark:    "#061535",
  navyMid:     "#143272",
  orange:      "#f97316",
  orangeHover: "#ea6c0a",
  bg:          "#eef3f9",
  card:        "#ffffff",
  border:      "#d4dce8",
  text:        "#0f172a",
  muted:       "#64748b",
  light:       "#e8eef5",
  success:     "#16a34a",
  danger:      "#dc2626",
  warn:        "#d97706",
};

// ── Constants ──────────────────────────────────────────────────────────────────
const STATIC_PATH   = "/home/demo/UserManagementModule/static/reference_files/";
const UPLOAD_URL    = `${baseUrl}/api/v2/user_modules/upload/`;
const DOWNLOAD_BASE = `${baseUrl}/static/reference_files/`;

const COUNTRIES = [
  "United States", "Canada", "United Kingdom", "Australia", "New Zealand",
  "Ireland", "Germany", "France", "Netherlands", "Belgium", "Switzerland",
  "Italy", "Spain", "Portugal", "Sweden", "Norway", "Denmark", "Finland",
  "Austria", "Poland", "Czech Republic", "Singapore", "Malaysia",
  "United Arab Emirates", "South Korea", "Japan", "China", "Hong Kong",
  "South Africa", "Other",
];

const INSTITUTION_TYPES = [
  "Public University",
  "Private University",
  "Community College",
  "Research Institution",
  "Other",
];

const PARTICIPATION_OPTIONS = [
  "Student Recruitment",
  "Academic Partnerships",
  "Faculty Exchange",
  "Research Collaboration",
  "Dual Degree / Articulation Programs",
  "Study Abroad Opportunities",
  "Internship / Industry Collaboration",
  "Other",
];

const RECRUITMENT_LEVELS = [
  "Undergraduate",
  "Graduate / Master's",
  "Doctoral",
  "Certificate / Diploma Programs",
];

const ALL_LEVELS_OPTION = "All of the Above";
const RECRUITMENT_LEVEL_OPTIONS = [...RECRUITMENT_LEVELS, ALL_LEVELS_OPTION];

const AREAS_OF_INTEREST = [
  "Engineering & Technology",
  "Business & Management",
  "Computer Science / AI",
  "Health Sciences",
  "Hospitality & Tourism",
  "Arts & Design",
  "Humanities & Social Sciences",
  "Data Science & Analytics",
  "Robotics & Automation",
  "Other",
];

// Upload config
const LOGO_CONFIG       = { key: "logo",     label: "University Logo",             hint: "PNG or JPG · Max 5 MB",                         accept: ".png,.jpg,.jpeg", maxMB: 5 };
const CAMPUS_CONFIG     = { key: "campus",   label: "Campus / Institution Photos", hint: "PNG/JPG · Multiple allowed · Max 10 MB each",   accept: ".png,.jpg,.jpeg", maxMB: 10, multiple: true };
const BROCHURE_CONFIG   = { key: "brochure", label: "Brochures / Flyers / PDFs",   hint: "PDF, DOCX · Multiple allowed · Max 10 MB each", accept: ".pdf,.docx,.doc", maxMB: 10, multiple: true };

// ── Shared UI Atoms ────────────────────────────────────────────────────────────
const Card = ({ children, style = {} }) => (
  <div style={{
    background: T.card, borderRadius: 14,
    border: `1px solid ${T.border}`,
    boxShadow: "0 4px 20px rgba(10,35,82,.07)",
    overflow: "hidden", ...style,
  }}>
    {children}
  </div>
);

const SectionHeader = ({ icon: Icon, title, badge, isOpen, onToggle }) => (
  <div
    onClick={onToggle}
    style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "14px 20px",
      background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyMid} 100%)`,
      cursor: "pointer", userSelect: "none",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{
        background: T.orange, borderRadius: 7, padding: "5px 6px",
        display: "flex", alignItems: "center",
      }}>
        <Icon size={15} color="#fff" />
      </div>
      <span style={{ color: "#fff", fontWeight: 800, fontSize: 14, letterSpacing: .2 }}>{title}</span>
      {badge != null && (
        <span style={{
          background: T.orange, color: "#fff",
          borderRadius: 99, padding: "1px 8px", fontSize: 11, fontWeight: 800,
        }}>{badge}</span>
      )}
    </div>
    {isOpen
      ? <ChevronUp size={18} color="rgba(255,255,255,.75)" />
      : <ChevronDown size={18} color="rgba(255,255,255,.75)" />}
  </div>
);

const Field = ({ label, required, children, hint, fullWidth, style: fieldStyle = {} }) => (
  <div style={{ marginBottom: 14, gridColumn: fullWidth ? "1/-1" : undefined, ...fieldStyle }}>
    <label style={{
      display: "block", fontSize: 12, fontWeight: 700, color: T.navy,
      marginBottom: 5, letterSpacing: .3, textTransform: "uppercase",
    }}>
      {label}{required && <span style={{ color: T.danger, marginLeft: 3 }}>*</span>}
    </label>
    {children}
    {hint && <p style={{ fontSize: 11, color: T.muted, margin: "4px 0 0" }}>{hint}</p>}
  </div>
);

const inputBase = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: `1.5px solid ${T.border}`, fontSize: 13, color: T.text,
  background: "#fff", outline: "none", boxSizing: "border-box",
  transition: "border .18s, box-shadow .18s", fontFamily: FONT,
};

const Input = ({ style = {}, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      style={{
        ...inputBase,
        borderColor: focused ? T.orange : T.border,
        boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none",
        ...style,
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

const Select = ({ children, style = {}, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <select
      {...props}
      style={{
        ...inputBase,
        borderColor: focused ? T.orange : T.border,
        boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none",
        ...style,
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {children}
    </select>
  );
};

const Textarea = ({ style = {}, maxLength, value, ...props }) => {
  const [focused, setFocused] = useState(false);
  const wordCount = value ? value.trim().split(/\s+/).filter(Boolean).length : 0;
  return (
    <div>
      <textarea
        value={value}
        {...props}
        style={{
          ...inputBase,
          borderColor: focused ? T.orange : T.border,
          boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none",
          resize: "vertical", minHeight: 90, ...style,
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      {maxLength && (
        <p style={{
          fontSize: 11, margin: "3px 0 0", textAlign: "right",
          color: wordCount > maxLength ? T.danger : T.muted,
        }}>
          {wordCount} / {maxLength} words
        </p>
      )}
    </div>
  );
};

const Btn = ({ children, variant = "primary", size = "md", icon: Icon, onClick, disabled, style = {} }) => {
  const [hovered, setHovered] = useState(false);
  const pad = size === "sm" ? "7px 14px" : "10px 22px";
  const fs = size === "sm" ? 12 : 13;
  const variants = {
    primary:   { background: hovered ? T.orangeHover : T.orange, color: "#fff", border: "none" },
    secondary: { background: hovered ? T.light : "#f1f5f9", color: T.navy, border: `1.5px solid ${T.border}` },
    navy:      { background: hovered ? T.navyMid : T.navy, color: "#fff", border: "none" },
    success:   { background: hovered ? "#15803d" : T.success, color: "#fff", border: "none" },
    outline:   {
      background: "transparent", color: T.navy, border: `1.5px solid ${T.border}`,
      ...(hovered && { borderColor: T.orange, color: T.orange }),
    },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        borderRadius: 8, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
        padding: pad, fontSize: fs, transition: "all .15s",
        opacity: disabled ? .6 : 1, fontFamily: FONT,
        ...variants[variant], ...style,
      }}
    >
      {Icon && <Icon size={size === "sm" ? 13 : 14} />}
      {children}
    </button>
  );
};

const Checkbox = ({ checked, onChange, label, required }) => (
  <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginBottom: 10 }}>
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1,
        border: `2px solid ${checked ? T.orange : T.border}`,
        background: checked ? T.orange : "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all .15s", cursor: "pointer",
      }}
    >
      {checked && <Check size={11} color="#fff" strokeWidth={3} />}
    </div>
    <span style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}>
      {label}
      {required && <span style={{ color: T.danger, marginLeft: 3 }}>*</span>}
    </span>
  </label>
);

// Generic multi-select chip group
const ChipSelect = ({ options, value, onChange }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
    {options.map(opt => {
      const active = value.includes(opt);
      return (
        <button
          key={opt}
          onClick={() => onChange(active ? value.filter(x => x !== opt) : [...value, opt])}
          style={{
            padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
            border: `1.5px solid ${active ? T.orange : T.border}`,
            background: active ? "rgba(249,115,22,.1)" : "#fff",
            color: active ? T.orange : T.muted,
            cursor: "pointer", transition: "all .15s", fontFamily: FONT,
          }}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

const ToggleSwitch = ({ value, onChange }) => (
  <div
    onClick={() => onChange(!value)}
    style={{
      width: 40, height: 22, borderRadius: 99, cursor: "pointer",
      background: value ? T.orange : T.border,
      position: "relative", transition: "background .2s", flexShrink: 0,
    }}
  >
    <div style={{
      position: "absolute", top: 3,
      left: value ? 20 : 3,
      width: 16, height: 16, borderRadius: "50%",
      background: "#fff", transition: "left .2s",
      boxShadow: "0 1px 4px rgba(0,0,0,.2)",
    }} />
  </div>
);

const DonutChart = ({ pct, size = 88 }) => {
  const r = 36, cx = 44, cy = 44;
  const circ = 2 * Math.PI * r;
  const filled = (pct / 100) * circ;
  return (
    <svg width={size} height={size} viewBox="0 0 88 88">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={T.light} strokeWidth={14} />
      <circle
        cx={cx} cy={cy} r={r} fill="none"
        stroke={pct > 80 ? T.success : pct > 50 ? T.orange : T.danger}
        strokeWidth={14}
        strokeDasharray={`${filled} ${circ - filled}`}
        style={{ transform: "rotate(-90deg)", transformOrigin: "center", transition: "stroke-dasharray .5s" }}
      />
      <text x="44" y="49" textAnchor="middle" fontSize="15" fontWeight="900" fill={T.navy}>{pct}%</text>
    </svg>
  );
};

const ProgressBar = ({ value, color = T.orange }) => (
  <div style={{ background: T.light, borderRadius: 99, height: 6, overflow: "hidden" }}>
    <div style={{
      width: `${Math.min(value, 100)}%`, height: "100%",
      background: color, borderRadius: 99, transition: "width .5s",
    }} />
  </div>
);

// ── Single File Upload Row ─────────────────────────────────────────────────────
const SingleFileRow = ({ config, uploaded, isUploading, onChoose, onClear }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 16, padding: "12px 16px",
    background: uploaded ? "rgba(249,115,22,.05)" : T.bg,
    borderRadius: 10,
    border: `1.5px dashed ${uploaded ? T.orange : T.border}`,
    fontFamily: FONT,
  }}>
    <div style={{
      background: uploaded ? "rgba(249,115,22,.15)" : T.light,
      borderRadius: 8, padding: 10, flexShrink: 0,
    }}>
      <Upload size={16} color={uploaded ? T.orange : T.navy} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: T.text }}>{config.label}</div>
      {isUploading ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
          <div style={{
            width: 11, height: 11, border: `2px solid ${T.light}`,
            borderTop: `2px solid ${T.orange}`, borderRadius: "50%",
            animation: "spin 0.7s linear infinite",
          }} />
          <span style={{ fontSize: 12, color: T.orange, fontWeight: 600 }}>Uploading…</span>
        </div>
      ) : uploaded ? (
        <a href={`${DOWNLOAD_BASE}${uploaded.fileName}`} target="_blank" rel="noopener noreferrer"
          style={{
            fontSize: 12, color: "#007bff", textDecoration: "underline",
            display: "block", marginTop: 3, overflow: "hidden",
            textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
          {uploaded.fileName}
        </a>
      ) : (
        <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{config.hint}</div>
      )}
    </div>
    {!isUploading && (
      uploaded ? (
        <button onClick={onClear} title="Remove file" style={{
          cursor: "pointer", background: "none", border: "none",
          color: T.danger, fontWeight: 800, fontSize: 15, lineHeight: 1,
          padding: "2px 7px", flexShrink: 0,
        }}>✕</button>
      ) : (
        <button onClick={onChoose} style={{
          background: "transparent", border: `1.5px solid ${T.border}`,
          borderRadius: 7, padding: "5px 14px", color: T.navy,
          fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center",
          gap: 5, flexShrink: 0, cursor: "pointer", fontFamily: FONT,
        }}>
          <Upload size={12} /> Choose File
        </button>
      )
    )}
  </div>
);

// ── Multi File Upload List ──────────────────────────────────────────────────────
const MultiFileList = ({ files, onRemove, onAdd, isUploading, config }) => (
  <div>
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
      {files.map((f, i) => (
        <div key={i} style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "8px 12px", background: "rgba(249,115,22,.05)",
          borderRadius: 8, border: `1px solid rgba(249,115,22,.2)`,
        }}>
          <FileText size={14} color={T.orange} />
          <a href={`${DOWNLOAD_BASE}${f.fileName}`} target="_blank" rel="noopener noreferrer"
            style={{
              flex: 1, fontSize: 12, color: "#007bff", textDecoration: "underline",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
            {f.fileName}
          </a>
          <button onClick={() => onRemove(i)} style={{
            background: "none", border: "none", color: T.danger,
            cursor: "pointer", padding: "0 4px", fontWeight: 800, fontSize: 13,
          }}>✕</button>
        </div>
      ))}
    </div>
    <div style={{
      display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
      background: T.bg, borderRadius: 10,
      border: `1.5px dashed ${T.border}`, fontFamily: FONT,
    }}>
      <div style={{ background: T.light, borderRadius: 8, padding: 8, flexShrink: 0 }}>
        <Upload size={15} color={T.navy} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: T.text }}>{config.label}</div>
        {isUploading ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
            <div style={{
              width: 10, height: 10, border: `2px solid ${T.light}`,
              borderTop: `2px solid ${T.orange}`, borderRadius: "50%",
              animation: "spin 0.7s linear infinite",
            }} />
            <span style={{ fontSize: 11, color: T.orange, fontWeight: 600 }}>Uploading…</span>
          </div>
        ) : (
          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{config.hint}</div>
        )}
      </div>
      {!isUploading && (
        <button onClick={onAdd} style={{
          background: "transparent", border: `1.5px solid ${T.border}`,
          borderRadius: 7, padding: "5px 14px", color: T.navy,
          fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center",
          gap: 5, cursor: "pointer", fontFamily: FONT,
        }}>
          <Plus size={12} /> Add File
        </button>
      )}
    </div>
  </div>
);

// ── Step indicator ─────────────────────────────────────────────────────────────
const STEPS = [
  { label: "Institution",     icon: Globe         },
  { label: "Representative",  icon: Users         },
  { label: "Delegates",       icon: UserPlus      },
  { label: "Participation",   icon: GraduationCap },
  { label: "Marketing",       icon: Image         },
  { label: "Declaration",     icon: Check         },
];

const StepBar = ({ current, onStepClick, isStepCompleted }) => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "18px 32px 0", gap: 0, fontFamily: FONT,
  }}>
    {STEPS.map((s, i) => {
      const done = isStepCompleted(i);
      const active = i === current;
      return (
        <React.Fragment key={i}>
          <div
            onClick={() => onStepClick(i)}
            style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 80, cursor: "pointer" }}
          >
            <div style={{
              width: 34, height: 34, borderRadius: "50%",
              background: done ? T.success : active ? T.orange : T.light,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: `2px solid ${done ? T.success : active ? T.orange : T.border}`,
              transition: "all .2s",
            }}>
              {done
                ? <Check size={15} color="#fff" strokeWidth={3} />
                : <s.icon size={14} color={active ? "#fff" : T.muted} />}
            </div>
            <span style={{
              fontSize: 10, fontWeight: 700, color: active ? T.orange : done ? T.success : T.muted,
              marginTop: 4, textAlign: "center", letterSpacing: .3, textTransform: "uppercase",
            }}>{s.label}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{
              flex: 1, height: 2, background: done ? T.success : T.light,
              margin: "-18px 4px 0", transition: "background .3s",
            }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────
function ForeignUniversityRegistration() {
  const uc = getUserCredentials();
  const [step, setStep]                       = useState(0);
  const [existingProfile, setExistingProfile] = useState(null);
  const [saving, setSaving]                   = useState(false);
  const [saveStatus, setSaveStatus]           = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [previewMode, setPreviewMode]         = useState(false);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [step]);

  const goToStep = (newStep) => {
    setStep(newStep);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 50);
  };

  // ── Form data ──────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    // 1. Institutional Information
    university_name: "",
    country: "",
    institution_website: "",
    year_established: "",
    institution_type: "",
    other_institution_type: "",
    accreditation_ranking: "",

    // 2. Primary Representative Details
    rep_full_name: "",
    rep_designation: "",
    rep_department: "",
    rep_email: "",
    rep_mobile: "",
    rep_whatsapp: "",
    rep_linkedin: "",

    // 4. Participation Interest
    participation_interest: [],
    participation_other: "",
    recruitment_levels: [],
    areas_of_interest: [],
    areas_other: "",

    // 6. Marketing & Promotional Information
    institutional_profile: "",
    instagram_handle: "",
    linkedin_page: "",
    facebook_page: "",
    youtube_channel: "",

    // 7. Previous Engagement with India
    recruited_from_india: false,
    india_engagement_experience: "",

    // 8. Compliance & Declaration
    consent_accuracy: false,
    consent_guidelines: false,
    consent_newsletter: false,
    authorized_rep_name: "",
    declaration_date: "",
  });

  const validateStep = (currentStep) => {
    switch (currentStep) {
      case 0:
        return (
          formData.university_name &&
          formData.country &&
          formData.institution_website &&
          formData.year_established &&
          formData.institution_type &&
          (formData.institution_type !== "Other" || formData.other_institution_type)
        );
      case 1:
        return (
          formData.rep_full_name &&
          formData.rep_designation &&
          formData.rep_email &&
          formData.rep_mobile &&
          formData.rep_linkedin
        );
      case 2:
        return delegates.every(d => d.name && d.designation && d.email && d.mobile);
      case 3:
        return (
          formData.participation_interest.length > 0 &&
          formData.recruitment_levels.length > 0 &&
          formData.areas_of_interest.length > 0
        );
      case 4:
        return (
          !!formData.institutional_profile &&
          !!logoFile &&
          (!formData.recruited_from_india || !!formData.india_engagement_experience)
        );
      case 5:
        return (
          formData.consent_accuracy &&
          formData.consent_guidelines &&
          formData.authorized_rep_name &&
          formData.declaration_date
        );
      default:
        return true;
    }
  };

  // Used for step-completion checkmarks (StepBar + sidebar Steps list)
  const isStepCompleted = (stepNo) => !!validateStep(stepNo);

  // ── Additional Delegates ─────────────────────────────────────────────────────
  const [delegates, setDelegates] = useState([{ name: "", designation: "", email: "", mobile: "" }]);
  const addDelegate    = () => setDelegates(p => [...p, { name: "", designation: "", email: "", mobile: "" }]);
  const removeDelegate = i  => setDelegates(p => p.filter((_, idx) => idx !== i));
  const updateDelegate = (i, key, value) => {
    const r = [...delegates];
    r[i][key] = value;
    setDelegates(r);
  };

  // ── File uploads ───────────────────────────────────────────────────────────
  const [logoFile, setLogoFile]           = useState(null);
  const [campusFiles, setCampusFiles]     = useState([]);
  const [brochureFiles, setBrochureFiles] = useState([]);
  const [uploadingKeys, setUploadingKeys]  = useState({});
  const logoRef       = useRef(null);
  const campusRef     = useRef(null);
  const brochureRef   = useRef(null);

  // ── Collapsible sections ───────────────────────────────────────────────────
  const [openSections, setOpenSections] = useState({
    institution: true, accreditation: true,
    representative: true, delegates: true,
    nature: true, levels: true, areas: true,
    profile: true, logo: true, campus: true, brochures: true,
    social: true, india: true,
    declaration: true,
  });
  const toggleSection = k => setOpenSections(p => ({ ...p, [k]: !p[k] }));

  // ── Load existing profile ──────────────────────────────────────────────────
  useEffect(() => { fetchExistingProfile(); }, []);

  const fetchExistingProfile = async () => {
    try {
      const creds = Fetchexecutequery();
      const res = await axios.post(creds[1], {
        query: `SELECT * FROM edu_platform.foreign_university_profile WHERE user_id = ${uc.user_id} AND is_active = 1 LIMIT 1;`,
      }, creds[0]);
      if (res.data && res.data.length > 0) {
        const row = res.data[0];
        setExistingProfile(row.primary_id);
        setFormData(prev => ({
          ...prev,
          university_name:              row.university_name              || "",
          country:                       row.country                       || "",
          institution_website:          row.institution_website           || "",
          year_established:             row.year_established              || "",
          institution_type:             row.institution_type              || "",
          other_institution_type:       row.other_institution_type        || "",
          accreditation_ranking:        row.accreditation_ranking         || "",
          rep_full_name:                row.rep_full_name                 || "",
          rep_designation:              row.rep_designation               || "",
          rep_department:               row.rep_department                || "",
          rep_email:                    row.rep_email                     || "",
          rep_mobile:                   row.rep_mobile                    || "",
          rep_whatsapp:                 row.rep_whatsapp                  || "",
          rep_linkedin:                 row.rep_linkedin                  || "",
          participation_interest:       row.participation_interest ? JSON.parse(row.participation_interest) : [],
          participation_other:          row.participation_other           || "",
          recruitment_levels:           row.recruitment_levels ? JSON.parse(row.recruitment_levels) : [],
          areas_of_interest:            row.areas_of_interest ? JSON.parse(row.areas_of_interest) : [],
          areas_other:                  row.areas_other                   || "",
          institutional_profile:        row.institutional_profile         || "",
          instagram_handle:             row.instagram_handle              || "",
          linkedin_page:                row.linkedin_page                 || "",
          facebook_page:                row.facebook_page                 || "",
          youtube_channel:              row.youtube_channel               || "",
          recruited_from_india:         !!row.recruited_from_india,
          india_engagement_experience:  row.india_engagement_experience   || "",
          consent_accuracy:             !!row.consent_accuracy,
          consent_guidelines:           !!row.consent_guidelines,
          consent_newsletter:           !!row.consent_newsletter,
          authorized_rep_name:          row.authorized_rep_name           || "",
          declaration_date:             row.declaration_date              || "",
        }));
        if (row.university_logo) setLogoFile({ fileName: row.university_logo });
        if (row.campus_photos) {
          try { setCampusFiles(JSON.parse(row.campus_photos).map(f => ({ fileName: f }))); }
          catch {}
        }
        if (row.brochures) {
          try { setBrochureFiles(JSON.parse(row.brochures).map(f => ({ fileName: f }))); }
          catch {}
        }

        if (row.additional_delegates) {
          try {
            const parsed = JSON.parse(row.additional_delegates);
            setDelegates(parsed.length ? parsed : [{ name: "", designation: "", email: "", mobile: "" }]);
          } catch {
            setDelegates([{ name: "", designation: "", email: "", mobile: "" }]);
          }
        }
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (name, value) =>
    setFormData(p => ({ ...p, [name]: value }));

  // ── File upload ────────────────────────────────────────────────────────────
  const uploadFile = async (key, maxMB, file) => {
    if (!file) return null;
    if (file.size > maxMB * 1024 * 1024) {
      alert(`File too large. Maximum size is ${maxMB} MB.`);
      return null;
    }
    setUploadingKeys(p => ({ ...p, [key]: true }));
    const fd = new FormData();
    fd.append("file", file);
    fd.append("filePath", STATIC_PATH);
    try {
      const res = await fetch(UPLOAD_URL, { method: "POST", body: fd });
      if (res.ok) {
        const data = await res.json();
        return { fileName: data.Filename, filePath: data.Filepath };
      } else {
        const err = await res.json();
        alert("Upload failed: " + (err.Message || "Unknown error"));
        return null;
      }
    } catch (err) {
      console.error("Upload error:", err);
      alert("Upload failed. Please try again.");
      return null;
    } finally {
      setUploadingKeys(p => ({ ...p, [key]: false }));
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const result = await uploadFile("logo", LOGO_CONFIG.maxMB, file);
    if (result) setLogoFile(result);
    if (logoRef.current) logoRef.current.value = "";
  };

  const handleCampusAdd = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      const result = await uploadFile("campus", CAMPUS_CONFIG.maxMB, file);
      if (result) setCampusFiles(p => [...p, result]);
    }
    if (campusRef.current) campusRef.current.value = "";
  };

  const handleBrochureAdd = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      const result = await uploadFile("brochure", BROCHURE_CONFIG.maxMB, file);
      if (result) setBrochureFiles(p => [...p, result]);
    }
    if (brochureRef.current) brochureRef.current.value = "";
  };

  // ── Completion ─────────────────────────────────────────────────────────────
  const requiredFields = [
    "university_name", "country", "institution_website", "institution_type", "year_established",
    "rep_full_name", "rep_designation", "rep_email", "rep_mobile", "rep_linkedin",
    "institutional_profile",
    "authorized_rep_name", "declaration_date",
  ];
  const delegatesValid = delegates.every(d => d.name && d.designation && d.email && d.mobile);
  const extraRequired = [
    { label: "Preferred Levels Of Recruitment", ok: formData.recruitment_levels.length > 0 },
    { label: "Areas Of Interest / Discipline",   ok: formData.areas_of_interest.length > 0 },
    { label: "University Logo",                  ok: !!logoFile },
    { label: "Delegate Details (all 4 fields)",  ok: delegatesValid },
    { label: "India Engagement Description",     ok: !formData.recruited_from_india || !!formData.india_engagement_experience },
  ];
  const filled = requiredFields.filter(f => {
    const v = formData[f];
    return v && String(v).trim() !== "";
  }).length;
  const completion = Math.round(
    ((filled + extraRequired.filter(e => e.ok).length) / (requiredFields.length + extraRequired.length)) * 100
  );
  const consentDone = formData.consent_accuracy && formData.consent_guidelines &&
    formData.authorized_rep_name && formData.declaration_date;
  const missingRequired = [
    ...requiredFields.filter(f => !formData[f] || String(formData[f]).trim() === ""),
    ...extraRequired.filter(e => !e.ok).map(e => e.label),
  ];

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async (status) => {
    setSaving(true);
    setSaveStatus(null);
    const now = getFormattedDate();
    const payload_data = {
      ...formData,
      participation_interest: JSON.stringify(formData.participation_interest),
      recruitment_levels:     JSON.stringify(formData.recruitment_levels),
      areas_of_interest:      JSON.stringify(formData.areas_of_interest),
      additional_delegates:   JSON.stringify(delegates),
      university_logo:        logoFile ? logoFile.fileName : "",
      campus_photos:           JSON.stringify(campusFiles.map(f => f.fileName)),
      brochures:               JSON.stringify(brochureFiles.map(f => f.fileName)),
      profile_status:         status,
      modified_date:          now,
      modified_by:            uc.user_id,
      user_id:                uc.user_id,
      is_active:              1,
    };
    try {
      if (existingProfile) {
        const creds = Fetchupdatequery();
        await axios.post(creds[1], {
          schema_name: "edu_platform",
          table: "foreign_university_profile",
          set_column: [payload_data],
          where: [{ primary_id: existingProfile }],
        }, creds[0]);
      } else {
        const creds = CreateData();
        await axios.post(creds[1], {
          database: "edu_platform",
          table: "foreign_university_profile",
          formData: {
            ...payload_data,
            tenant_id:    uc.tenants_id,
            entity_id:    uc.selectedentity || uc.entities_id,
            group_id:     uc.groups_id,
            created_by:   uc.user_id,
            created_date: now,
          },
        }, creds[0]);
        await fetchExistingProfile();
      }
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 3500);
    } catch (err) {
      console.error("Save error:", err);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: T.navy }}>
          <div style={{
            width: 44, height: 44, border: `4px solid ${T.light}`,
            borderTop: `4px solid ${T.orange}`, borderRadius: "50%",
            animation: "spin 0.8s linear infinite", margin: "0 auto 12px",
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ fontWeight: 700, fontSize: 14, margin: 0, fontFamily: FONT }}>Loading…</p>
        </div>
      </div>
    );
  }

  // ── Step renderers ─────────────────────────────────────────────────────────

  // STEP 0 — Institutional Information
  const renderStep0 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <Card>
        <SectionHeader icon={Building} title="Institutional Information" isOpen={openSections.institution} onToggle={() => toggleSection("institution")} />
        {openSections.institution && (
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "repeat(3, minmax(250px,1fr))", gap: 16 }}>
            <Field label="Name of the University / Institution" required fullWidth>
              <Input value={formData.university_name} onChange={e => handleChange("university_name", e.target.value)} placeholder="Enter full institution name" />
            </Field>
            <Field label="Country" required>
              <Select value={formData.country} onChange={e => handleChange("country", e.target.value)}>
                <option value="">Select country</option>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Institution Website" required>
              <Input value={formData.institution_website} onChange={e => handleChange("institution_website", e.target.value)} placeholder="https://www.youruniversity.edu" />
            </Field>
            <Field label="Year Established" required>
              <Input type="number" value={formData.year_established} onChange={e => handleChange("year_established", e.target.value)} placeholder="e.g. 1965" min="1000" max={new Date().getFullYear()} />
            </Field>
            <Field label="Type of Institution" required>
              <Select value={formData.institution_type} onChange={e => handleChange("institution_type", e.target.value)}>
                <option value="">Select institution type</option>
                {INSTITUTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </Select>
            </Field>
            {formData.institution_type === "Other" && (
              <Field label="Specify Institution Type" required fullWidth>
                <Input value={formData.other_institution_type} onChange={e => handleChange("other_institution_type", e.target.value)} placeholder="Describe institution type" />
              </Field>
            )}
          </div>
        )}
      </Card>

      <Card>
        <SectionHeader icon={BookOpen} title="Institutional Accreditation / Ranking" isOpen={openSections.accreditation} onToggle={() => toggleSection("accreditation")} />
        {openSections.accreditation && (
          <div style={{ padding: 24 }}>
            <Field label="Institutional Accreditation / Ranking" hint="e.g. AACSB, ABET, QS World Ranking, US News Ranking — if applicable" fullWidth>
              <Textarea value={formData.accreditation_ranking} onChange={e => handleChange("accreditation_ranking", e.target.value)} placeholder="List relevant accreditations or rankings" />
            </Field>
          </div>
        )}
      </Card>
    </div>
  );

  // STEP 1 — Primary Representative Details
  const renderStep1 = () => (
    <Card>
      <SectionHeader icon={Users} title="Primary Representative Details" isOpen={openSections.representative} onToggle={() => toggleSection("representative")} />
      {openSections.representative && (
        <div style={{ padding: 24, display: "grid", gridTemplateColumns: "repeat(3, minmax(250px,1fr))", gap: 16 }}>
          <Field label="Full Name" required>
            <Input value={formData.rep_full_name} onChange={e => handleChange("rep_full_name", e.target.value)} placeholder="Full name" />
          </Field>
          <Field label="Designation / Title" required>
            <Input value={formData.rep_designation} onChange={e => handleChange("rep_designation", e.target.value)} placeholder="e.g. Director, International Recruitment" />
          </Field>
          <Field label="Department">
            <Input value={formData.rep_department} onChange={e => handleChange("rep_department", e.target.value)} placeholder="e.g. Office of International Admissions" />
          </Field>
          <Field label="Official Email Address" required>
            <Input type="email" value={formData.rep_email} onChange={e => handleChange("rep_email", e.target.value)} placeholder="name@university.edu" />
          </Field>
          <Field label="Mobile Number (including country code)" required>
            <Input value={formData.rep_mobile} onChange={e => handleChange("rep_mobile", e.target.value)} placeholder="+1-XXXXXXXXXX" />
          </Field>
          <Field label="WhatsApp Number (if different)">
            <Input value={formData.rep_whatsapp} onChange={e => handleChange("rep_whatsapp", e.target.value)} placeholder="+1-XXXXXXXXXX" />
          </Field>
          <Field label="LinkedIn Profile" required fullWidth>
            <div style={{ display: "flex" }}>
              <div style={{
                background: T.light, border: `1.5px solid ${T.border}`, borderRight: "none",
                borderRadius: "8px 0 0 8px", padding: "9px 12px",
                display: "flex", alignItems: "center",
              }}>
                <LinkIcon size={14} color={T.muted} />
              </div>
              <Input
                value={formData.rep_linkedin}
                onChange={e => handleChange("rep_linkedin", e.target.value)}
                placeholder="https://linkedin.com/in/..."
                style={{ borderRadius: "0 8px 8px 0" }}
              />
            </div>
          </Field>
        </div>
      )}
    </Card>
  );

  // STEP 2 — Additional Delegates
  const renderStep2 = () => (
    <Card>
      <SectionHeader
        icon={UserPlus}
        title="Additional Delegates"
        badge={delegates.length}
        isOpen={openSections.delegates}
        onToggle={() => toggleSection("delegates")}
      />
      {openSections.delegates && (
        <div style={{ padding: 24 }}>
          <p style={{ fontSize: 12, color: T.muted, margin: "0 0 16px", fontFamily: FONT }}>
            Add details of additional delegates (ICRs) attending or representing your institution. All fields for each delegate are required.
          </p>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: T.navy, textTransform: "uppercase", letterSpacing: .6, paddingBottom: 6, borderBottom: `2px solid ${T.light}`, flex: 1, marginRight: 16, fontFamily: FONT }}>
              Delegates ({delegates.length})
            </div>
            <Btn variant="outline" size="sm" icon={UserPlus} onClick={addDelegate}>Add Delegate</Btn>
          </div>

          {/* Column headers */}
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 40px",
            gap: 10, marginBottom: 8, padding: "0 13px", boxSizing: "border-box",
          }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: T.navy, fontFamily: FONT }}>Full Name<span style={{ color: T.danger, marginLeft: 3 }}>*</span></div>
            <div style={{ fontWeight: 700, fontSize: 12, color: T.navy, fontFamily: FONT }}>Designation / Title<span style={{ color: T.danger, marginLeft: 3 }}>*</span></div>
            <div style={{ fontWeight: 700, fontSize: 12, color: T.navy, fontFamily: FONT }}>Official Email<span style={{ color: T.danger, marginLeft: 3 }}>*</span></div>
            <div style={{ fontWeight: 700, fontSize: 12, color: T.navy, fontFamily: FONT }}>Mobile (with country code)<span style={{ color: T.danger, marginLeft: 3 }}>*</span></div>
            <div />
          </div>

          {delegates.map((d, i) => (
            <div key={i} style={{
              display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 40px",
              gap: 10, marginBottom: 10, padding: 12,
              background: T.bg, borderRadius: 8, border: `1px solid ${T.border}`,
              boxSizing: "border-box",
            }}>
              <Input placeholder="Full name" value={d.name} onChange={e => updateDelegate(i, "name", e.target.value)} />
              <Input placeholder="Designation / Title" value={d.designation} onChange={e => updateDelegate(i, "designation", e.target.value)} />
              <Input placeholder="Email address" type="email" value={d.email} onChange={e => updateDelegate(i, "email", e.target.value)} />
              <Input placeholder="+1-XXXXXXXXXX" value={d.mobile} onChange={e => updateDelegate(i, "mobile", e.target.value)} />
              <button
                onClick={() => removeDelegate(i)}
                disabled={delegates.length === 1}
                style={{
                  width: 40, height: "100%",
                  background: delegates.length === 1 ? T.light : "rgba(220,38,38,.1)",
                  border: "none", borderRadius: 8,
                  cursor: delegates.length === 1 ? "not-allowed" : "pointer",
                  color: T.danger, display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <Minus size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );

  // STEP 3 — Participation Interest
  const renderStep3 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <Card>
        <SectionHeader icon={GraduationCap} title="Nature of Participation" isOpen={openSections.nature} onToggle={() => toggleSection("nature")} />
        {openSections.nature && (
          <div style={{ padding: 24 }}>
            <Field label="Nature of Participation" required hint="Select all that apply">
              <ChipSelect
                options={PARTICIPATION_OPTIONS}
                value={formData.participation_interest}
                onChange={v => handleChange("participation_interest", v)}
              />
            </Field>
            {formData.participation_interest.includes("Other") && (
              <Field label="Please specify" fullWidth>
                <Input value={formData.participation_other} onChange={e => handleChange("participation_other", e.target.value)} placeholder="Describe other participation interest" />
              </Field>
            )}
          </div>
        )}
      </Card>

      <Card>
        <SectionHeader icon={Users} title="Preferred Levels of Recruitment" badge="Required" isOpen={openSections.levels} onToggle={() => toggleSection("levels")} />
        {openSections.levels && (
          <div style={{ padding: 24 }}>
            {RECRUITMENT_LEVEL_OPTIONS.map(level => (
              <Checkbox
                key={level}
                checked={formData.recruitment_levels.includes(level)}
                onChange={checked => {
                  if (level === ALL_LEVELS_OPTION) {
                    handleChange("recruitment_levels", checked ? RECRUITMENT_LEVEL_OPTIONS : []);
                  } else {
                    const next = checked
                      ? [...formData.recruitment_levels.filter(x => x !== ALL_LEVELS_OPTION), level]
                      : formData.recruitment_levels.filter(x => x !== level);
                    handleChange("recruitment_levels", next);
                  }
                }}
                label={level}
                required={level === RECRUITMENT_LEVEL_OPTIONS[0]}
              />
            ))}
          </div>
        )}
      </Card>

      <Card>
        <SectionHeader icon={BookOpen} title="Areas / Disciplines of Interest" badge="Required" isOpen={openSections.areas} onToggle={() => toggleSection("areas")} />
        {openSections.areas && (
          <div style={{ padding: 24 }}>
            <Field label="Areas / Disciplines of Interest" required hint="Select all that apply">
              <ChipSelect
                options={AREAS_OF_INTEREST}
                value={formData.areas_of_interest}
                onChange={v => handleChange("areas_of_interest", v)}
              />
            </Field>
            {formData.areas_of_interest.includes("Other") && (
              <Field label="Please specify" fullWidth>
                <Input value={formData.areas_other} onChange={e => handleChange("areas_other", e.target.value)} placeholder="Describe other area of interest" />
              </Field>
            )}
          </div>
        )}
      </Card>
    </div>
  );

  // STEP 4 — Marketing & Promotional Information + Previous Engagement with India
  const renderStep4 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <Card>
        <SectionHeader icon={FileText} title="Institutional Profile" isOpen={openSections.profile} onToggle={() => toggleSection("profile")} />
        {openSections.profile && (
          <div style={{ padding: 24 }}>
            <Field label="Short Institutional Profile" hint="Please provide a short institutional profile (100–150 words)" fullWidth>
              <Textarea
                value={formData.institutional_profile}
                onChange={e => handleChange("institutional_profile", e.target.value)}
                placeholder="Write a brief profile of your institution..."
                maxLength={150}
                style={{ minHeight: 130 }}
              />
            </Field>
          </div>
        )}
      </Card>

      <Card>
        <SectionHeader icon={Image} title="University Logo" isOpen={openSections.logo} onToggle={() => toggleSection("logo")} />
        {openSections.logo && (
          <div style={{ padding: 20 }}>
            <input ref={logoRef} type="file" accept={LOGO_CONFIG.accept} style={{ display: "none" }} onChange={handleLogoChange} />
            <SingleFileRow
              config={LOGO_CONFIG}
              uploaded={logoFile}
              isUploading={!!uploadingKeys.logo}
              onChoose={() => logoRef.current?.click()}
              onClear={() => setLogoFile(null)}
            />
          </div>
        )}
      </Card>

      <Card>
        <SectionHeader icon={Image} title="Campus / Institution Photos" badge={campusFiles.length || undefined} isOpen={openSections.campus} onToggle={() => toggleSection("campus")} />
        {openSections.campus && (
          <div style={{ padding: 20 }}>
            <input ref={campusRef} type="file" accept={CAMPUS_CONFIG.accept} multiple style={{ display: "none" }} onChange={handleCampusAdd} />
            <MultiFileList
              config={CAMPUS_CONFIG}
              files={campusFiles}
              isUploading={!!uploadingKeys.campus}
              onAdd={() => campusRef.current?.click()}
              onRemove={i => setCampusFiles(p => p.filter((_, idx) => idx !== i))}
            />
          </div>
        )}
      </Card>

      <Card>
        <SectionHeader icon={FileText} title="Brochures / Promotional Materials" badge={brochureFiles.length || undefined} isOpen={openSections.brochures} onToggle={() => toggleSection("brochures")} />
        {openSections.brochures && (
          <div style={{ padding: 20 }}>
            <input ref={brochureRef} type="file" accept={BROCHURE_CONFIG.accept} multiple style={{ display: "none" }} onChange={handleBrochureAdd} />
            <MultiFileList
              config={BROCHURE_CONFIG}
              files={brochureFiles}
              isUploading={!!uploadingKeys.brochure}
              onAdd={() => brochureRef.current?.click()}
              onRemove={i => setBrochureFiles(p => p.filter((_, idx) => idx !== i))}
            />
          </div>
        )}
      </Card>

      <Card>
        <SectionHeader icon={Globe} title="Official Social Media Handles" isOpen={openSections.social} onToggle={() => toggleSection("social")} />
        {openSections.social && (
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {[
              { key: "instagram_handle", label: "Instagram", icon: Globe,   placeholder: "@youruniversity" },
              { key: "linkedin_page",    label: "LinkedIn",  icon: LinkIcon, placeholder: "https://linkedin.com/school/..." },
              { key: "facebook_page",    label: "Facebook",  icon: LinkIcon, placeholder: "https://facebook.com/youruniversity" },
              { key: "youtube_channel",  label: "YouTube",   icon: LinkIcon, placeholder: "https://youtube.com/@youruniversity" },
            ].map(s => (
              <Field key={s.key} label={`${s.label} (optional)`}>
                <div style={{ display: "flex" }}>
                  <div style={{
                    background: T.light, border: `1.5px solid ${T.border}`, borderRight: "none",
                    borderRadius: "8px 0 0 8px", padding: "9px 12px",
                    display: "flex", alignItems: "center",
                  }}>
                    <s.icon size={14} color={T.muted} />
                  </div>
                  <Input
                    value={formData[s.key]}
                    onChange={e => handleChange(s.key, e.target.value)}
                    placeholder={s.placeholder}
                    style={{ borderRadius: "0 8px 8px 0" }}
                  />
                </div>
              </Field>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <SectionHeader icon={Globe} title="Previous Engagement with India" isOpen={openSections.india} onToggle={() => toggleSection("india")} />
        {openSections.india && (
          <div style={{ padding: 24 }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "14px 18px", marginBottom: formData.recruited_from_india ? 20 : 0,
              background: formData.recruited_from_india ? "rgba(249,115,22,.05)" : T.bg,
              borderRadius: 10,
              border: `1.5px solid ${formData.recruited_from_india ? "rgba(249,115,22,.25)" : T.border}`,
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Has your institution previously recruited students from India?</div>
              </div>
              <ToggleSwitch value={formData.recruited_from_india} onChange={v => handleChange("recruited_from_india", v)} />
            </div>

            {formData.recruited_from_india && (
              <Field label="Engagement Experience in India" hint="Briefly mention your engagement experience in India" fullWidth>
                <Textarea
                  value={formData.india_engagement_experience}
                  onChange={e => handleChange("india_engagement_experience", e.target.value)}
                  placeholder="Describe your past recruitment / partnership experience in India..."
                />
              </Field>
            )}
          </div>
        )}
      </Card>
    </div>
  );

  // STEP 5 — Compliance & Declaration
  const renderStep5 = () => (
    <Card>
      <SectionHeader icon={Check} title="Compliance & Declaration" isOpen={openSections.declaration} onToggle={() => toggleSection("declaration")} />
      {openSections.declaration && (
        <div style={{ padding: 24 }}>
          <div style={{
            background: "rgba(249,115,22,.06)", borderRadius: 10,
            border: `1px solid rgba(249,115,22,.2)`, padding: "16px 20px",
            marginBottom: 24,
          }}>
            <p style={{ fontSize: 13, color: T.text, margin: 0, lineHeight: 1.7, fontFamily: FONT }}>
              Please read each declaration carefully before confirming your submission. Both consents and the authorized representative details are required to complete registration.
            </p>
          </div>

          <Checkbox
            checked={formData.consent_accuracy}
            onChange={v => handleChange("consent_accuracy", v)}
            required
            label="We confirm that the information provided above is accurate and complete."
          />
          <Checkbox
            checked={formData.consent_guidelines}
            onChange={v => handleChange("consent_guidelines", v)}
            required
            label="We agree to abide by the event guidelines and timelines communicated by the host institution."
          />
          <Checkbox
            checked={formData.consent_newsletter }
            onChange={v => handleChange("consent_newsletter", v)}
            required
            label="I would like to receive newsletters, event updates, future events, and other communications from the organizers."
          />
          <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "repeat(3, minmax(200px,1fr))", gap: 16 }}>
            <Field label="Name of Authorized Person" required>
              <Input
                value={formData.authorized_rep_name}
                onChange={e => handleChange("authorized_rep_name", e.target.value)}
                placeholder="Full name"
              />
            </Field>
            <Field label="Date" required>
              <Input
                type="date"
                value={formData.declaration_date}
                onChange={e => handleChange("declaration_date", e.target.value)}
              />
            </Field>
          </div>

          {!consentDone && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "rgba(220,38,38,.07)", borderRadius: 8,
              padding: "10px 14px", marginTop: 16,
            }}>
              <AlertCircle size={15} color={T.danger} />
              <span style={{ fontSize: 12, color: T.danger, fontWeight: 600, fontFamily: FONT }}>
                Both declarations and the authorized representative details must be provided to submit registration.
              </span>
            </div>
          )}
          {consentDone && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "rgba(22,163,74,.1)", borderRadius: 8,
              padding: "10px 14px", marginTop: 16,
            }}>
              <CheckCircle size={15} color={T.success} />
              <span style={{ fontSize: 12, color: T.success, fontWeight: 700, fontFamily: FONT }}>
                All declarations accepted. Ready to submit.
              </span>
            </div>
          )}
        </div>
      )}
    </Card>
  );

  // ── Preview Modal ──────────────────────────────────────────────────────────
  const PreviewModal = () => (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(6,21,53,.55)",
      zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24,
    }}>
      <div style={{
        background: T.card, borderRadius: 16, maxWidth: 640,
        width: "100%", maxHeight: "85vh", overflow: "auto",
        boxShadow: "0 24px 60px rgba(6,21,53,.3)",
        fontFamily: FONT,
      }}>
        <div style={{
          background: `linear-gradient(135deg, ${T.navy}, ${T.navyMid})`,
          padding: "18px 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0,
        }}>
          <span style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>Submission Preview</span>
          <button onClick={() => setPreviewMode(false)} style={{
            background: "rgba(255,255,255,.15)", border: "none", borderRadius: 6,
            padding: "4px 12px", color: "#fff", cursor: "pointer", fontWeight: 700, fontSize: 12,
          }}>Close</button>
        </div>
        <div style={{ padding: 24 }}>
          {[
            ["University / Institution", formData.university_name],
            ["Country", formData.country],
            ["Institution Website", formData.institution_website || "—"],
            ["Year Established", formData.year_established || "—"],
            ["Institution Type", formData.institution_type === "Other" ? formData.other_institution_type : formData.institution_type],
            ["Accreditation / Ranking", formData.accreditation_ranking || "—"],
            ["Primary Representative", formData.rep_full_name],
            ["Designation", formData.rep_designation],
            ["Department", formData.rep_department || "—"],
            ["Representative Email", formData.rep_email],
            ["Representative Mobile", formData.rep_mobile],
            ["Additional Delegates", delegates.filter(d => d.name).length || "0"],
            ["Nature of Participation", formData.participation_interest.join(", ") || "—"],
            ["Preferred Levels of Recruitment", formData.recruitment_levels.join(", ") || "—"],
            ["Areas of Interest", formData.areas_of_interest.join(", ") || "—"],
            ["Recruited from India before", formData.recruited_from_india ? "Yes" : "No"],
            ["Authorized Representative", formData.authorized_rep_name],
            ["Declaration Date", formData.declaration_date],
            ["University Logo", logoFile ? logoFile.fileName : "Not uploaded"],
            ["Campus Photos", campusFiles.length > 0 ? `${campusFiles.length} file(s)` : "None"],
            ["Brochures", brochureFiles.length > 0 ? `${brochureFiles.length} file(s)` : "None"],
            ["Consents Given", consentDone ? "Yes" : "No"],
          ].map(([k, v]) => (
            <div key={k} style={{
              display: "flex", padding: "9px 0",
              borderBottom: `1px solid ${T.light}`,
            }}>
              <span style={{ width: 200, fontSize: 12, color: T.muted, fontWeight: 700, flexShrink: 0 }}>{k}</span>
              <span style={{ fontSize: 13, color: T.text, flex: 1 }}>{v || "—"}</span>
            </div>
          ))}
          <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn variant="secondary" onClick={() => setPreviewMode(false)}>Go Back & Edit</Btn>
            <Btn variant="primary" icon={Check} onClick={() => { setPreviewMode(false); handleSave("submitted"); }} disabled={!consentDone || saving}>
              {saving ? "Submitting…" : "Confirm & Submit"}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Full render ────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: FONT }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
      `}</style>

      {previewMode && <PreviewModal />}

      <h2 className="pt-1 pb-1 text-center" style={{ color: T.navy, fontSize: "18px", margin: 0, padding: "16px 0 0" }}>
        FOREIGN UNIVERSITY REGISTRATION
      </h2>

      <div style={{ width: "100%", padding: "24px 24px 48px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 24, alignItems: "start" }}>

          {/* ── Form area ── */}
          <div>
            <StepBar
              current={step}
              onStepClick={(clickedStep) => {
                if (clickedStep <= step || validateStep(step)) {
                  goToStep(clickedStep);
                }
              }}
              isStepCompleted={isStepCompleted}
            />

            <div style={{ marginTop: 18 }}>
              {step === 0 && renderStep0()}
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
              {step === 4 && renderStep4()}
              {step === 5 && renderStep5()}
            </div>

            {/* Navigation */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginTop: 24, paddingBottom: 8,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {step > 0 && (
                  <Btn variant="outline" onClick={() => goToStep(step - 1)}>← Back</Btn>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {saveStatus === "saved" && (
                  <span style={{ display: "flex", alignItems: "center", gap: 6, color: T.success, fontWeight: 700, fontSize: 13 }}>
                    <CheckCircle size={15} /> Saved
                  </span>
                )}
                {saveStatus === "error" && (
                  <span style={{ display: "flex", alignItems: "center", gap: 6, color: T.danger, fontWeight: 700, fontSize: 13 }}>
                    <AlertCircle size={15} /> Save failed
                  </span>
                )}
                <Btn variant="secondary" icon={Save} onClick={() => handleSave("draft")} disabled={saving}>
                  Save Draft
                </Btn>
                {step < STEPS.length - 1 ? (
                  <Btn
                    variant="primary"
                    disabled={!validateStep(step)}
                    onClick={() => setStep(s => s + 1)}
                  >
                    Next →
                  </Btn>
                ) : (
                  <>
                    <Btn variant="navy" icon={Eye} onClick={() => setPreviewMode(true)}>
                      Preview
                    </Btn>
                    <Btn
                      variant="success"
                      icon={Check}
                      onClick={() => handleSave("submitted")}
                      disabled={saving || !consentDone}
                    >
                      {saving ? "Submitting…" : existingProfile ? "Update Registration" : "Submit Registration"}
                    </Btn>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ── Sidebar ── */}
          <div style={{ position: "sticky", top: 24 }}>
            <Card style={{ padding: 20, marginBottom: 18 }}>
              <div style={{
                fontWeight: 800, fontSize: 13, color: T.navy, marginBottom: 14,
                textTransform: "uppercase", letterSpacing: .5,
              }}>
                Profile Completion
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 14 }}>
                <DonutChart pct={completion} />
              </div>
              <ProgressBar value={completion} color={completion > 80 ? T.success : T.orange} />

              {missingRequired.length > 0 && (
                <div style={{ marginTop: 18 }}>
                  <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>
                    Required Fields Missing
                  </div>
                  {missingRequired.map(f => (
                    <div key={f} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.danger, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: T.text }}>
                        {f.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {missingRequired.length === 0 && (
                <div style={{
                  marginTop: 14, display: "flex", alignItems: "center", gap: 6,
                  background: "rgba(22,163,74,.1)", borderRadius: 8, padding: "8px 12px",
                }}>
                  <CheckCircle size={14} color={T.success} />
                  <span style={{ fontSize: 12, color: T.success, fontWeight: 700 }}>
                    All required fields filled
                  </span>
                </div>
              )}

              <div style={{ marginTop: 18, borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>
                  Status
                </div>
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: existingProfile ? "rgba(249,115,22,.1)" : T.light,
                  borderRadius: 8, padding: "6px 12px",
                }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: existingProfile ? T.orange : T.muted }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: existingProfile ? T.orange : T.muted }}>
                    {existingProfile ? "Profile Exists" : "Not Yet Saved"}
                  </span>
                </div>
              </div>

              {/* Delegates / Participation summary */}
              <div style={{ marginTop: 14, borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>
                  Summary
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: T.muted, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: T.muted }}>Delegates: {delegates.filter(d => d.name).length}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: T.muted, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: T.muted }}>Participation: {formData.participation_interest.length}</span>
                </div>
              </div>

              {/* Declaration status */}
              <div style={{ marginTop: 14, borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>
                  Declaration
                </div>
                {[
                  ["Accuracy", formData.consent_accuracy],
                  ["Guidelines", formData.consent_guidelines],
                  ["Signatory", !!formData.authorized_rep_name],
                ].map(([label, val]) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: val ? T.success : T.border, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: val ? T.success : T.muted, fontWeight: val ? 700 : 400 }}>{label}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Step guide */}
            <Card style={{ padding: 16 }}>
              <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 10 }}>
                Steps
              </div>
              {STEPS.map((s, i) => (
                <div
                  key={i}
                  onClick={() => { if (i <= step || validateStep(step)) { goToStep(i); } }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 10px", borderRadius: 8, marginBottom: 4,
                    cursor: "pointer",
                    background: step === i ? "rgba(249,115,22,.1)" : "transparent",
                    border: `1px solid ${step === i ? "rgba(249,115,22,.3)" : "transparent"}`,
                    transition: "all .15s",
                  }}
                >
                  <div style={{
                    width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                    background: isStepCompleted(i) ? T.success : step === i ? T.orange : T.light,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {isStepCompleted(i)
                      ? <Check size={11} color="#fff" strokeWidth={3} />
                      : <s.icon size={11} color={step === i ? "#fff" : T.muted} />}
                  </div>
                  <span style={{
                    fontSize: 12, fontWeight: step === i ? 700 : 500,
                    color: step === i ? T.orange : isStepCompleted(i) ? T.success : T.muted,
                  }}>{s.label}</span>
                </div>
              ))}
            </Card>
          </div>

        </div>
      </div>
    </div>
  );
}

export default ForeignUniversityRegistration;